'use client';

/**
 * PricingPlans Component
 *
 * NOTA: Le seguenti funzionalità sono temporaneamente nascoste perché non ancora implementate:
 * - GMV pricing (calcolatore fasce + add-on) — vedi useShopifyGMV.ts
 * - Banner beta / early access
 * - "Monthly AI usage" sezione completa (inclusi "Included AI credits" e "Used AI credits")
 * - "Extra calls" display (calcolo chiamate oltre il limite)
 * - "Usage-based billing" toggle e configurazione
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useQuery } from '@apollo/client';
import { GET_CONNECTORS } from '../../utils/gql/queries';
import { ConnectorType } from '../../utils/gql/types';
import { OnboardingRequestModal } from 'src/components/Settings/OnboardingRequestModal';
import { useTrialStatus } from '../../hooks/useTrialStatus';

/** Mappa currentPlan API → nome card UI. trial/custom non hanno card dedicata. */
function planKeyToCardName(currentPlan: string | undefined): string | null {
  switch ((currentPlan || '').toLowerCase()) {
    case 'free':
      return 'Free';
    case 'starter':
      return 'Starter';
    case 'pro':
      return 'Pro';
    case 'advanced':
      return 'Advanced';
    default:
      return null;
  }
}

function formatCurrentPlanLabel(currentPlan: string | undefined): string {
  const key = (currentPlan || '').toLowerCase();
  if (key === 'trial') return 'Free Trial';
  if (!key) return 'Unknown';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// Palette
const emerald = '#10b981';
const emeraldLight = '#34d399';
const navy = '#061829';

// Motion wrapper
const MotionCard = motion.create(Card);

// Free plan (visualizzato separatamente sopra i piani a pagamento)
const freePlan = {
  name: 'Free',
  basePrice: 0,
  includedCalls: 20,
  lookbackLabel: '90 days',
  features: [
    '1 user',
    'All connectors enabled',
    '20 AI credits/month included',
    '90 days historical data lookback',
    'Weekly Snapshot email every week',
    'Custom reports',
    'Inventory tool',
    'KPI Dashboard',
    'Finance (New)',
  ],
};

// Base plans (prezzi in dollari)
const plans = [
  {
    name: 'Starter',
    basePrice: 29, // $29/30 giorni
    includedCalls: 250,
    lookbackLabel: '12 months',
    features: [
      '1 user',
      'All connectors enabled',
      '250 AI credits/month included',
      '12 months historical data lookback',
      'Weekly + Monthly email reports',
      'Custom reports',
      'Inventory tool',
      'KPI Dashboard',
      'Finance (New)',
    ],
  },
  {
    name: 'Pro',
    basePrice: 59, // $59/30 giorni
    includedCalls: 1000,
    lookbackLabel: '24 months',
    features: [
      'Unlimited users',
      'All connectors enabled',
      '1,000 AI credits/month included',
      '24 months historical data lookback',
      'Daily + Weekly + Monthly email reports',
      'Custom reports',
      'Inventory tool',
      'KPI Dashboard',
      'Finance (New)',
    ],
  },
  {
    name: 'Advanced',
    basePrice: 149, // $149/30 giorni
    includedCalls: 3000,
    lookbackLabel: 'Unlimited',
    features: [
      'Unlimited users',
      'All connectors enabled',
      '3,000 AI credits/month included',
      'Unlimited historical data lookback',
      'Daily + Weekly + Monthly email reports',
      'Custom email sources (KPI / Custom report)',
      'Custom reports',
      'Inventory tool',
      'KPI Dashboard',
      'Finance (New)',
      'Early access to new features',
    ],
  },
];

export default function PricingPlans() {
  const { data: connectorsData } = useQuery(GET_CONNECTORS);
  const { currentPlan, loading: planLoading } = useTrialStatus();
  // Source of truth: piano reale da getTrialStatus (non stato UI marketing)
  const currentCard = planLoading ? null : planKeyToCardName(currentPlan);
  const currentPlanLabel = formatCurrentPlanLabel(currentPlan);
  const shopDomain = connectorsData?.getConnectors?.find(
    (c: { connectorType: string }) => c.connectorType === ConnectorType.SHOPIFY,
  )?.displayName;
  const shopHandle = shopDomain
    ? String(shopDomain).replace(/\.myshopify\.com$/i, '')
    : null;
  const shopifyPricingUrl =
    shopHandle &&
    `https://admin.shopify.com/store/${shopHandle}/charges/kipify-1/pricing_plans`;

  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isFreeCurrent = currentCard === 'Free';

  return (
    <Box
      component="section"
      sx={{
        bgcolor: 'f1f5f9',
        py: { xs: 2, sm: 3, md: 4, lg: 5 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Title */}
      <Box
        sx={{
          mb: { xs: 3, sm: 4, md: 5, lg: 6 },
          textAlign: 'center',
          width: '100%',
          px: { xs: 1, sm: 2, md: 3, lg: 4 },
        }}
      >
        <Typography
          variant="h2"
          align="center"
          fontWeight={800}
          sx={{
            color: '#000',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem', lg: '2.5rem' },
            textAlign: 'center',
            mx: 'auto',
            lineHeight: 1.2,
            maxWidth: '100%',
            wordBreak: 'break-word',
          }}
        >
          Pricing Plans
        </Typography>
        {!planLoading && (
          <Typography
            variant="body2"
            align="center"
            sx={{
              color: '#6b7280',
              mt: 1,
              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
            }}
          >
            Your current plan: {currentPlanLabel}
          </Typography>
        )}
      </Box>

      {/* Card dei piani - Grid responsive ottimizzato */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: { xs: 2, sm: 2.5, md: 2.5, lg: 2 },
          width: '100%',
          alignItems: 'stretch',
          mb: { xs: 4, sm: 5, md: 6, lg: 8 },
          px: { xs: 1, sm: 2, md: 3, lg: 4 },
          boxSizing: 'border-box',
        }}
      >
        {/* Free card: stesso linguaggio visivo delle altre cards */}
        <MotionCard
          whileHover={{
            scale: isMobile ? 1 : 1.02,
            boxShadow: `0 8px 32px 0 ${emeraldLight}55`,
          }}
          whileTap={{
            scale: isMobile ? 0.98 : 0.97,
            boxShadow: `0 2px 8px 0 ${emeraldLight}99`,
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: { xs: 'auto', sm: '420px', md: '440px', lg: '460px' },
            px: { xs: 1.5, sm: 1.75, md: 2, lg: 2.25 },
            pt: { xs: 2, sm: 2.25, md: 2.5, lg: 2.75 },
            pb: 0,
            borderRadius: { xs: 2, sm: 3, md: 4 },
            border: '1px solid',
            borderColor: isFreeCurrent ? emeraldLight : '#e5e7eb',
            boxShadow: isFreeCurrent
              ? `0 2px 12px 0 ${emeraldLight}22`
              : '0 1px 2px rgba(0,0,0,0.04)',
            transition: 'all .15s cubic-bezier(.4,0,.2,1)',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            bgcolor: '#fff',
          }}
        >
          <CardContent
            sx={{
              p: 0,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 1.5,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: '#111827',
                  fontSize: {
                    xs: '1.125rem',
                    sm: '1.25rem',
                    md: '1.375rem',
                    lg: '1.5rem',
                  },
                }}
              >
                {freePlan.name}
              </Typography>
              <Chip
                label={isFreeCurrent ? 'Current plan' : 'No monthly charge'}
                size="small"
                sx={{
                  bgcolor: isFreeCurrent ? '#d1fae5' : '#dbeafe',
                  color: isFreeCurrent ? emerald : '#1d4ed8',
                  fontWeight: 700,
                  fontSize: {
                    xs: '0.65rem',
                    sm: '0.7rem',
                    md: '0.75rem',
                  },
                  px: { xs: 0.75, sm: 1, md: 1.25 },
                  py: { xs: 0.2, sm: 0.35 },
                  borderRadius: 999,
                  boxShadow: 'none',
                  border: 'none',
                  letterSpacing: 0,
                  alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: '1.5rem',
                    sm: '1.65rem',
                    md: '1.85rem',
                    lg: '2rem',
                  },
                  fontWeight: 800,
                  color: emerald,
                }}
              >
                $0
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#6b7280',
                  mb: 0.5,
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                }}
              >
                /month
              </Typography>
            </Box>

            <Box
              component="ul"
              sx={{ pl: 0, mb: 0, listStyle: 'none', flexGrow: 1 }}
            >
              {freePlan.features.map((feat) => (
                <Box
                  key={feat}
                  component="li"
                  sx={{ display: 'flex', alignItems: 'start', mb: 1.1 }}
                >
                  <Check
                    size={isMobile ? 14 : isTablet ? 16 : 18}
                    strokeWidth={2.5}
                    style={{
                      color: emerald,
                      marginRight: isMobile ? 6 : 8,
                      marginTop: 2,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#374151',
                        lineHeight: 1.375,
                        fontSize: {
                          xs: '0.75rem',
                          sm: '0.78rem',
                          md: '0.8rem',
                          lg: '0.82rem',
                        },
                        wordBreak: 'break-word',
                      }}
                    >
                      {feat.replace(' (New)', '')}
                    </Typography>
                    {feat.includes('(New)') && (
                      <Chip
                        label="NEW"
                        size="small"
                        sx={{
                          bgcolor: '#ff9800',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: { xs: '0.6rem', sm: '0.65rem' },
                          height: 18,
                          px: 0.5,
                          '& .MuiChip-label': {
                            px: 0.5,
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
          <Box sx={{ p: 0, mt: 1.75, mb: 1.75, mx: 0 }}>
            <Button
              fullWidth
              component={shopifyPricingUrl ? 'a' : 'button'}
              href={shopifyPricingUrl || undefined}
              target={shopifyPricingUrl ? '_blank' : undefined}
              rel={shopifyPricingUrl ? 'noopener noreferrer' : undefined}
              sx={{
                width: '100%',
                py: { xs: 0.95, sm: 1.05, md: 1.15 },
                borderRadius: { xs: 2, sm: 3, md: 4 },
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.78rem', md: '0.82rem' },
                textTransform: 'none',
                border: `2px solid ${emerald}`,
                bgcolor: isFreeCurrent ? emerald : navy,
                color: isFreeCurrent ? '#fff' : emerald,
                boxShadow: isFreeCurrent
                  ? `0 2px 8px 0 ${emerald}33`
                  : 'none',
                transition: 'all .15s cubic-bezier(.4,0,.2,1)',
                outline: isFreeCurrent
                  ? `2px solid ${emeraldLight}`
                  : 'none',
                '&:hover': {
                  bgcolor: isFreeCurrent ? emeraldLight : '#0e223a',
                  color: '#fff',
                  boxShadow: `0 4px 16px 0 ${emeraldLight}55`,
                },
                '&:active': {
                  bgcolor: isFreeCurrent ? '#059669' : '#061829',
                  boxShadow: `0 1px 4px 0 ${emerald}99`,
                  color: '#fff',
                },
                m: 0,
              }}
            >
              {isFreeCurrent ? 'Current plan' : 'Choose'}
            </Button>
          </Box>
        </MotionCard>

        {plans.map((plan) => {
          const isActive = plan.name === currentCard;
          const planPrice = plan.basePrice;

          return (
            <MotionCard
              key={plan.name}
              whileHover={{
                scale: isMobile ? 1 : 1.02,
                boxShadow: `0 8px 32px 0 ${emeraldLight}55`,
              }}
              whileTap={{
                scale: isMobile ? 0.98 : 0.97,
                boxShadow: `0 2px 8px 0 ${emeraldLight}99`,
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: {
                  xs: 'auto',
                  sm: '420px',
                  md: '440px',
                  lg: '460px',
                },
                px: { xs: 1.5, sm: 1.75, md: 2, lg: 2.25 },
                pt: { xs: 2, sm: 2.25, md: 2.5, lg: 2.75 },
                pb: 0,
                borderRadius: { xs: 2, sm: 3, md: 4 },
                border: '1px solid',
                borderColor: isActive ? emeraldLight : '#e5e7eb',
                boxShadow: isActive
                  ? `0 2px 12px 0 ${emeraldLight}22`
                  : '0 1px 2px rgba(0,0,0,0.04)',
                transition: 'all .15s cubic-bezier(.4,0,.2,1)',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              <CardContent
                sx={{
                  p: 0,
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      color: '#111827',
                      fontSize: {
                        xs: '1.125rem',
                        sm: '1.25rem',
                        md: '1.375rem',
                        lg: '1.5rem',
                      },
                    }}
                  >
                    {plan.name}
                  </Typography>
                  {isActive ? (
                    <Chip
                      label="Current plan"
                      size="small"
                      sx={{
                        bgcolor: '#d1fae5',
                        color: emerald,
                        fontWeight: 700,
                        fontSize: {
                          xs: '0.65rem',
                          sm: '0.7rem',
                          md: '0.75rem',
                        },
                        px: { xs: 0.75, sm: 1, md: 1.25 },
                        py: { xs: 0.2, sm: 0.35 },
                        borderRadius: 999,
                        boxShadow: 'none',
                        border: 'none',
                        letterSpacing: 0,
                        alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                      }}
                    />
                  ) : plan.name === 'Advanced' ? (
                    <Chip
                      label="Early Access"
                      size="small"
                      sx={{
                        bgcolor: '#8b5cf6',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: {
                          xs: '0.65rem',
                          sm: '0.7rem',
                          md: '0.75rem',
                        },
                        px: { xs: 0.75, sm: 1, md: 1.25 },
                        py: { xs: 0.2, sm: 0.35 },
                        borderRadius: 999,
                        boxShadow: 'none',
                        border: 'none',
                        letterSpacing: 0,
                        alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                      }}
                    />
                  ) : null}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    mb: plan.name === 'Advanced' ? 0.75 : 1.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '1.5rem',
                        sm: '1.65rem',
                        md: '1.85rem',
                        lg: '2rem',
                      },
                      fontWeight: 800,
                      color: emerald,
                    }}
                  >
                    ${planPrice}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#6b7280',
                      mb: 0.5,
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                    }}
                  >
                    /month
                  </Typography>
                </Box>

                {plan.name === 'Advanced' && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#8b5cf6',
                      fontSize: {
                        xs: '0.58rem',
                        sm: '0.62rem',
                        md: '0.66rem',
                      },
                      fontWeight: 600,
                      mb: 1.75,
                      fontStyle: 'italic',
                    }}
                  >
                    ✨ Get new features first
                  </Typography>
                )}

                <Box
                  component="ul"
                  sx={{ pl: 0, mb: 0, listStyle: 'none', flexGrow: 1 }}
                >
                  {plan.features.map((feat) => (
                    <Box
                      key={feat}
                      component="li"
                      sx={{ display: 'flex', alignItems: 'start', mb: 1.1 }}
                    >
                      <Check
                        size={isMobile ? 14 : isTablet ? 16 : 18}
                        strokeWidth={2.5}
                        style={{
                          color: emerald,
                          marginRight: isMobile ? 6 : 8,
                          marginTop: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#374151',
                            lineHeight: 1.375,
                            fontSize: {
                              xs: '0.75rem',
                              sm: '0.78rem',
                              md: '0.8rem',
                              lg: '0.82rem',
                            },
                            wordBreak: 'break-word',
                          }}
                        >
                          {feat.replace(' (New)', '')}
                        </Typography>
                        {feat.includes('(New)') && (
                          <Chip
                            label="NEW"
                            size="small"
                            sx={{
                              bgcolor: '#ff9800',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: 18,
                              px: 0.5,
                              '& .MuiChip-label': {
                                px: 0.5,
                              },
                            }}
                          />
                        )}
                        {feat.includes('Early access') && (
                          <Chip
                            label="EXCLUSIVE"
                            size="small"
                            sx={{
                              bgcolor: '#8b5cf6',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: 18,
                              px: 0.5,
                              '& .MuiChip-label': {
                                px: 0.5,
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
              <Box sx={{ p: 0, mt: 1.75, mb: 1.75, mx: 0 }}>
                <Button
                  fullWidth
                  component={shopifyPricingUrl ? 'a' : 'button'}
                  href={shopifyPricingUrl || undefined}
                  target={shopifyPricingUrl ? '_blank' : undefined}
                  rel={shopifyPricingUrl ? 'noopener noreferrer' : undefined}
                  sx={{
                    width: '100%',
                    py: { xs: 0.95, sm: 1.05, md: 1.15 },
                    borderRadius: { xs: 2, sm: 3, md: 4 },
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.78rem', md: '0.82rem' },
                    textTransform: 'none',
                    border: `2px solid ${emerald}`,
                    bgcolor: isActive ? emerald : navy,
                    color: isActive ? '#fff' : emerald,
                    boxShadow: isActive ? `0 2px 8px 0 ${emerald}33` : 'none',
                    transition: 'all .15s cubic-bezier(.4,0,.2,1)',
                    outline: isActive ? `2px solid ${emeraldLight}` : 'none',
                    '&:hover': {
                      bgcolor: isActive ? emeraldLight : '#0e223a',
                      color: '#fff',
                      boxShadow: `0 4px 16px 0 ${emeraldLight}55`,
                    },
                    '&:active': {
                      bgcolor: isActive ? '#059669' : '#061829',
                      boxShadow: `0 1px 4px 0 ${emerald}99`,
                      color: '#fff',
                    },
                    m: 0,
                  }}
                >
                  {isActive ? 'Current plan' : 'Choose'}
                </Button>
              </Box>
            </MotionCard>
          );
        })}
      </Box>

      {/* Free onboarding — available on every plan */}
      <Box
        sx={{
          mx: { xs: 1, sm: 2, md: 3, lg: 4 },
          mb: { xs: 2, sm: 3, md: 4 },
          p: { xs: 2.25, sm: 2.75, md: 3.25 },
          borderRadius: { xs: 2, sm: 3, md: 4 },
          border: `1px solid ${emerald}33`,
          background: `linear-gradient(135deg, ${navy} 0%, #0a2a3f 55%, #0d3d4a 100%)`,
          boxShadow: `0 12px 40px ${navy}22`,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              mb: 1.25,
            }}
          >
            <Chip
              label="Limited-time offer"
              size="small"
              sx={{
                bgcolor: `${emerald}22`,
                color: emeraldLight,
                fontWeight: 700,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                border: `1px solid ${emerald}55`,
              }}
            />
            <Chip
              label="Exclusive · while spots last"
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 700,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                border: '1px solid rgba(255,255,255,0.18)',
              }}
            />
          </Box>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: '1.15rem', sm: '1.35rem', md: '1.5rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
              mb: 0.75,
            }}
          >
            Free live onboarding — we set Kipify up with you
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.78)',
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' },
              lineHeight: 1.5,
              maxWidth: 640,
            }}
          >
            Connectors, the metrics that matter, and a weekly rhythm that
            actually drives decisions. Open to every plan for a limited window
            only — this isn&apos;t a permanent free service. Book now while we
            still have capacity and leave with a setup that pays for itself.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'stretch', md: 'flex-end' },
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={() => setOnboardingOpen(true)}
            sx={{
              py: { xs: 1.1, sm: 1.2 },
              px: { xs: 2.5, sm: 3 },
              borderRadius: 999,
              fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              textTransform: 'none',
              bgcolor: emerald,
              color: '#fff',
              whiteSpace: 'nowrap',
              boxShadow: `0 8px 24px ${emerald}44`,
              '&:hover': {
                bgcolor: emeraldLight,
                color: navy,
                boxShadow: `0 10px 28px ${emerald}66`,
              },
            }}
          >
            Book free onboarding
          </Button>
          <Typography
            sx={{
              color: emeraldLight,
              fontSize: { xs: '0.72rem', sm: '0.78rem' },
              fontWeight: 600,
              textAlign: { xs: 'center', md: 'right' },
            }}
          >
            Takes ~30 seconds · we reply by email
          </Typography>
        </Box>
      </Box>

      <OnboardingRequestModal
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        storeHint={shopHandle}
      />

      {/* Nascondo completamente Monthly AI usage per evitare confusione */}
      {/* <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: { xs: '100%', sm: 600, md: 700 },
          mx: 'auto',
          px: { xs: 1, sm: 2, md: 3, lg: 4 },
          boxSizing: 'border-box'
        }}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            color: '#111827',
            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem', lg: '1.75rem' },
            textAlign: 'center',
            mx: 'auto',
            mb: { xs: 2, sm: 3, md: 4 }
          }}
        >
          Monthly AI usage
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            gap: { xs: 2, sm: 3, md: 4 },
            flexWrap: 'wrap',
            mb: { xs: 3, sm: 4, md: 5 },
            width: '100%',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              fontWeight: 500,
              color: '#374151',
              textAlign: { xs: 'center', sm: 'left' },
              whiteSpace: 'nowrap'
            }}
          >
            Included AI credits:{' '}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: '#111827' }}
            >
              {currentPlan.includedCalls.toLocaleString()}
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              fontWeight: 500,
              color: '#374151',
              textAlign: { xs: 'center', sm: 'left' },
              whiteSpace: 'nowrap'
            }}
          >
            Used AI credits:{' '}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: '#111827' }}
            >
              {usedCalls.toLocaleString()}
            </Box>
          </Typography>
        </Box>
        
        <Card
          variant="outlined"
          sx={{
            maxWidth: { xs: '100%', sm: 500, md: 600 },
            width: '100%',
            mx: 'auto',
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: { xs: 2, sm: 3, md: 4 },
            borderColor: '#e5e7eb',
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ 
              mb: 3, 
              color: '#111827', 
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.125rem' } 
            }}
          >
            Usage-based billing
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Typography sx={{ 
              color: '#374151', 
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } 
            }}>
              Enable usage-based billing
            </Typography>
            <Switch
              checked={usageBilling}
              onChange={(e) => setUsageBilling(e.target.checked)}
              sx={{
                '& .Mui-checked': { color: emerald },
                '& .Mui-checked + .MuiSwitch-track': {
                  bgcolor: emerald,
                },
              }}
            />
          </Box>
          {usageBilling && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1.2,
                  color: '#374151',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  fontWeight: 500,
                }}
              >
                Monthly spending limit (€):
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={spendLimit}
                onChange={(e) => setSpendLimit(e.target.value)}
                placeholder="Ex: 50"
                inputProps={{
                  style: {
                    padding: '10px 14px',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    fontWeight: 600,
                    textAlign: 'left',
                  },
                  min: 0,
                  inputMode: 'decimal',
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: { xs: 2, sm: 3 },
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    padding: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#b5e3d6',
                    borderWidth: 1.5,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#10b981',
                  },
                  mt: 0,
                }}
              />
            </Box>
          )}
          <Button
            fullWidth
            sx={{
              width: '100%',
              bgcolor: emerald,
              '&:hover': { bgcolor: '#0e9f6e' },
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
              textTransform: 'none',
              py: { xs: 1, sm: 1.2 },
              borderRadius: { xs: 2, sm: 3 },
            }}
            disabled={!usageBilling}
            onClick={() => alert('Saved!')}
          >
            Save
          </Button>
        </Card>
      </Box> */}
    </Box>
  );
}
